import * as Styled from "./ScrollableTable.styles";
import React, { useMemo, useState } from "react";
import {
  Box,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Table,
  Tooltip,
} from "@mui/material";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

export type ColumnDef = {
  id: string;
  label: string;
  align?: "left" | "center" | "right";
  sortable?: boolean;
  tooltip?: string;
};

export type ScrollableTableProps<T> = {
  columns: ColumnDef[];
  rows: T[];
  onRowClick?: (row: T) => void;
  maxHeight?: string;
  rowKey?: keyof T;
};

const ScrollableTable = <T extends object>({
  columns,
  rows,
  onRowClick,
  maxHeight = "472px",
  rowKey,
}: ScrollableTableProps<T>) => {
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [isAscending, setIsAscending] = useState(true);

  const getRowKey = (row: T, index: number) => {
    if (rowKey && row[rowKey]) {
      return String(row[rowKey]);
    }
    return index;
  };

  const handleSort = (column: ColumnDef) => {
    if (column.sortable === false) return;

    if (sortColumn === column.id) {
      setIsAscending((prevIsAscending) => !prevIsAscending);
    } else {
      setSortColumn(column.id);
      setIsAscending(true);
    }
  };

  const sortedRows = useMemo(() => {
    if (!sortColumn) return rows;

    return [...rows].sort((rowA, rowB) => {
      const valueA = rowA[sortColumn as keyof T];
      const valueB = rowB[sortColumn as keyof T];

      if (valueA == null && valueB == null) return 0;
      if (valueA == null) return isAscending ? -1 : 1;
      if (valueB == null) return isAscending ? 1 : -1;

      let comparison = 0;
      if (typeof valueA === "number" && typeof valueB === "number") {
        comparison = valueA - valueB;
      } else {
        comparison = String(valueA).localeCompare(String(valueB), undefined, { numeric: true, sensitivity: "base" });
      }

      return isAscending ? comparison : -comparison;
    });
  }, [rows, sortColumn, isAscending]);

  return (
    <TableContainer component={Styled.TableContainerWrapper} sx={{ maxHeight }}>
      <Table stickyHeader aria-label="sortable table">
        <TableHead>
          <TableRow>
            {columns.map((column) => (
              <Styled.HeaderCell
                key={column.id}
                align={column.align || "left"}
                sortable={column.sortable !== false}
                onClick={() => handleSort(column)}
              >
                <Styled.HeaderCellContent>
                  <span>{column.label}</span>
                  {column.sortable !== false && (
                    <Box sx={{ display: "flex", gap: "4px", alignItems: "center" }}>
                      {column.tooltip && (
                        <Tooltip title={column.tooltip} arrow>
                          <span style={{ display: "flex" }}>
                            <InfoOutlinedIcon sx={{ fontSize: "16px", color: "inherit" }} />
                          </span>
                        </Tooltip>
                      )}
                        <KeyboardArrowUpIcon
                          sx={{
                            fontSize: "16px",
                            color:
                              sortColumn === column.id && isAscending
                                ? "primary.main"
                                : "inherit",
                          }}
                        />
                        <KeyboardArrowDownIcon
                          sx={{
                            fontSize: "16px",
                            color:
                              sortColumn === column.id && !isAscending
                                ? "primary.main"
                                : "inherit",
                          }}
                        />
                    </Box>
                  )}
                </Styled.HeaderCellContent>
              </Styled.HeaderCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedRows.map((row, index) => (
            <Styled.BodyRow
              key={getRowKey(row, index)}
              isClickable={!!onRowClick}
              onClick={() => onRowClick?.(row)}
            >
              {columns.map((column) => {
                const cellValue = row[column.id as keyof T];

                return (
                  <TableCell key={column.id} align={column.align || "left"}>
                    <Styled.BodyCellContent>
                      {cellValue as React.ReactNode}
                    </Styled.BodyCellContent>
                  </TableCell>
                );
              })}
            </Styled.BodyRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default ScrollableTable;
